import React, { useEffect, useRef, useState } from 'react'
import { CNav, CNavItem, CNavLink } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../shared/icons'
import { useBostonState, emit } from './lib/useBoston'
import { devReviewPollingEnabled, devReviewPollingIntervalMs } from '../../services/config'
import { devQueuePendingSummary } from './lib/biaApi'
import TokenBar from './TokenBar'
import AiBuildView from './views/AiBuildView'
import AiImportView from './views/AiImportView'
import AiPromptView from './views/AiPromptView'
import AiUsageView from './views/AiUsageView'
import AiKnowledgeView from './views/AiKnowledgeView'
import DevReviewView from './views/DevReviewView'
import CustomScriptView from './views/CustomScriptView'
import AnatomyView from './views/AnatomyView'
import EditorView from './views/EditorView'
import ApisView from './views/ApisView'
import ConsoleView from './views/ConsoleView'
import LibraryView from './views/LibraryView'
import RemarksView from './views/RemarksView'
import ConfigView from './views/ConfigView'
import AIAdminView from './views/AIAdminView'

const DEV_REVIEW_SEEN_KEY = 'boston_dev_review_seen_ids_v1'
const DEV_REVIEW_CHANGED_EVENT = 'boston-dev-review-queue-changed'
const DEV_QUEUE_KEY = 'boston_dev_queue_v1'

function readSeenReviewIds() {
  try {
    const value = JSON.parse(localStorage.getItem(DEV_REVIEW_SEEN_KEY) || '[]')
    return new Set(Array.isArray(value) ? value.map((id) => String(id)) : [])
  } catch (_) {
    return new Set()
  }
}

function saveSeenReviewIds(ids) {
  try {
    localStorage.setItem(DEV_REVIEW_SEEN_KEY, JSON.stringify(Array.from(new Set(ids || [])).slice(-200)))
  } catch (_) {
    /* no-op */
  }
}

function localReviewSummary() {
  try {
    const items = JSON.parse(localStorage.getItem(DEV_QUEUE_KEY) || '[]')
    const pending = Array.isArray(items)
      ? items.filter((item) => String(item?.estado || 'en_revision').trim().toLowerCase() === 'en_revision')
      : []
    return { count: pending.length, ids: pending.map((item) => String(item.id || '')).filter(Boolean), unseen: 0 }
  } catch (_) {
    return { count: 0, ids: [], unseen: 0 }
  }
}

/* Misma estructura de navegacion que boston_ia (NAV_ITEMS), agrupada. */
const NAV_GROUPS = [
  {
    title: 'Inteligencia Artificial',
    items: [
      { id: 'aiBuild', label: 'IA · Construir', icon: 'cilLightbulb' },
      { id: 'aiImport', label: 'IA · Importar archivos', icon: 'cilCloudUpload' },
      { id: 'aiPrompt', label: 'Prompts de agentes', icon: 'cilNotes' },
      { id: 'aiKnowledge', label: 'Base de conocimiento', icon: 'cilBook' },
      { id: 'aiUsage', label: 'Uso de tokens IA', icon: 'cilDollar' },
      { id: 'devReview', label: 'Revision Dev', icon: 'cilBellExclamation' },
      { id: 'aiAdmin', label: 'Admin AI', icon: 'cilSettings' },
    ],
  },
  {
    title: 'Custom Script Builder',
    items: [
      { id: 'custom', label: 'Custom Script', icon: 'cilPlus' },
      { id: 'anatomy', label: 'Anatomia del script', icon: 'cilInfo' },
      { id: 'editor', label: 'Editar existente', icon: 'cilPencil' },
    ],
  },
  {
    title: 'Operacion Sabre',
    items: [
      { id: 'console', label: 'Consola GDS', icon: 'cilTerminal' },
      { id: 'apis', label: 'APIs Sabre', icon: 'cilBolt' },
      { id: 'remarks', label: 'Remarks', icon: 'cilEnvelopeClosed' },
      { id: 'library', label: 'Biblioteca', icon: 'cilFolderOpen' },
    ],
  },
  {
    title: 'Sistema',
    items: [{ id: 'config', label: 'Configuracion', icon: 'cilSettings' }],
  },
]

const VIEW_MAP = {
  aiBuild: AiBuildView,
  aiImport: AiImportView,
  aiPrompt: AiPromptView,
  aiUsage: AiUsageView,
  aiKnowledge: AiKnowledgeView,
  devReview: DevReviewView,
  aiAdmin: AIAdminView,
  custom: CustomScriptView,
  anatomy: AnatomyView,
  editor: EditorView,
  apis: ApisView,
  console: ConsoleView,
  library: LibraryView,
  remarks: RemarksView,
  config: ConfigView,
}

function BostonIAPage({ onError }) {
  const state = useBostonState()
  // La vista activa vive en state.view (igual que el router original de boston_ia):
  // asi las vistas pueden navegar entre si mutando state.view + emit()
  // (ej. "Cargar en el Builder" desde Editor / Revision Dev / IA).
  const view = VIEW_MAP[state.view] ? state.view : 'custom'
  const Active = VIEW_MAP[view] || CustomScriptView
  const viewRef = useRef(view)
  const [reviewNotice, setReviewNotice] = useState(() => localReviewSummary())
  viewRef.current = view

  function refreshLocalReviewNotice() {
    const summary = localReviewSummary()
    const seen = readSeenReviewIds()
    const unseen = view === 'devReview' ? 0 : summary.ids.filter((id) => !seen.has(String(id))).length
    if (view === 'devReview') saveSeenReviewIds(summary.ids)
    setReviewNotice({ ...summary, unseen })
  }

  function applyReviewSummary(summary) {
    const ids = summary.ids || []
    const seen = readSeenReviewIds()
    const currentView = viewRef.current
    const unseen = currentView === 'devReview' ? 0 : ids.filter((id) => !seen.has(String(id))).length
    if (currentView === 'devReview') saveSeenReviewIds(ids)
    setReviewNotice({ count: Number(summary.count || 0), ids, unseen, via: summary.via || '', warn: summary.warn || '' })
  }

  function markCurrentReviewsSeen(notice = reviewNotice) {
    const ids = notice.ids || []
    if (!ids.length) return
    saveSeenReviewIds(ids)
    setReviewNotice((current) => ({ ...current, unseen: 0 }))
  }

  useEffect(() => {
    let debounceTimer = null
    const onQueueChanged = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(refreshLocalReviewNotice, 200)
    }
    const onStorage = (event) => {
      if (event.key === DEV_QUEUE_KEY || event.key === DEV_REVIEW_SEEN_KEY) onQueueChanged()
    }

    refreshLocalReviewNotice()
    window.addEventListener(DEV_REVIEW_CHANGED_EVENT, onQueueChanged)
    window.addEventListener('storage', onStorage)

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer)
      window.removeEventListener(DEV_REVIEW_CHANGED_EVENT, onQueueChanged)
      window.removeEventListener('storage', onStorage)
    }
  }, [view])

  useEffect(() => {
    if (!devReviewPollingEnabled()) return undefined
    let alive = true
    const intervalMs = devReviewPollingIntervalMs()

    async function refreshRemoteReviewNotice() {
      try {
        const summary = await devQueuePendingSummary({ minIntervalMs: intervalMs })
        if (alive) applyReviewSummary(summary)
      } catch (_) {
        /* no-op: el helper ya hace fallback local */
      }
    }

    refreshRemoteReviewNotice()
    const timer = window.setInterval(refreshRemoteReviewNotice, intervalMs)

    return () => {
      alive = false
      window.clearInterval(timer)
    }
  }, [])

  function go(id) {
    state.view = id
    if (id === 'devReview') markCurrentReviewsSeen()
    emit()
  }

  return (
    <div className="bia-wrap">
      <TokenBar />
      <div className="bia-layout">
        <aside className="bia-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.title} className="bia-nav-group">
              <div className="bia-nav-title">{group.title}</div>
              <CNav variant="pills" className="flex-column">
                {group.items.map((item) => (
                  <CNavItem key={item.id}>
                    <CNavLink active={view === item.id} onClick={() => go(item.id)} className="bia-nav-link">
                      <CIcon icon={icon(item.icon)} className="me-2" />
                      <span className="bia-nav-label">{item.label}</span>
                      {item.id === 'devReview' && reviewNotice.count > 0 ? (
                        <span
                          className={`bia-review-count ${reviewNotice.unseen > 0 ? 'new' : ''}`}
                          title={`${reviewNotice.count} revision(es) en_revision${reviewNotice.unseen > 0 ? `, ${reviewNotice.unseen} nueva(s)` : ''}`}
                        >
                          {reviewNotice.count}
                        </span>
                      ) : null}
                    </CNavLink>
                  </CNavItem>
                ))}
              </CNav>
            </div>
          ))}
        </aside>
        <section className="bia-content">
          <Active onError={onError} />
        </section>
      </div>
    </div>
  )
}

export default BostonIAPage
